import type { Permission, PlatformRole, Role, SubscriptionStatus } from '@kortex/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';
import { clearNativeWebData } from './androidBridge';
import { deviceIdForAuth } from './deviceId';
import { ACCESS_REVOKED_EVENT, closePosDataSource } from './posData';

export interface PropertySummary {
  id: string;
  name: string;
  code: string;
  city?: string;
  role: Role;
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  mustChangePassword?: boolean;
  /** Whether this account asks for an authenticator code at sign-in. */
  twoFactorEnabled?: boolean;
}

export interface SubscriptionSummary {
  planCode: string;
  planName: string;
  planDescription: string | null;
  /** True once a mandate is set up, whether or not it has charged yet. */
  /** When the 7-day grace window closes. Null unless one is open. */
  graceEndsAt: string | null;
  /** True once a standing instruction is set up, charged or not yet. */
  autoRenew: boolean;
  priceMonthly: number;
  /** What the plan advertises, whether or not this hotel is still in it. */
  trialDays: number;
  status: SubscriptionStatus;
  statusLabel: string;
  isEntitled: boolean;
  /**
   * What the account may do (apps/api subscription/access.ts): FULLY_ACTIVE,
   * READ_ONLY (lapsed, reading and exporting for 30 days), EXPORT_ONLY
   * (closing), OFFBOARDING, SUSPENDED, LAPSED. Absent on an older server.
   */
  accessState?: 'FULLY_ACTIVE' | 'READ_ONLY' | 'EXPORT_ONLY' | 'OFFBOARDING' | 'SUSPENDED' | 'LAPSED';
  accessUntil?: string | null;
  daysRemaining: number;
  endsAt: string | null;
  /**
   * Paid up to here. Null until a payment settles, which is what tells a
   * brand-new hotel apart from one that has been paying for a year.
   */
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  limits: { maxProperties: number; maxUsers: number };
  /**
   * How much of that allowance is gone. Null on an older server that does not
   * send it, so every reader has to cope with its absence.
   */
  usage: { properties: number; users: number } | null;
  features: string[];
}

export interface Session {
  user: SessionUser;
  property: { id: string; name: string; code?: string; gstin: string; stateCode: string } | null;
  role: Role | null;
  permissions: Permission[];
  platformRole: PlatformRole;
  properties: PropertySummary[];
}

export interface CaptchaFields {
  captchaId?: string;
  captchaAnswer?: string;
  /** Google's token, when reCAPTCHA is the challenge in use. */
  captchaToken?: string;
}

export interface RegisterInput {
  business: Record<string, string>;
  owner: Record<string, string>;
  planCode?: string;
  /** The billing period chosen at signup. Recorded, never charged - the trial
   *  still runs first, and this only decides what the billing page opens on. */
  billingOptionId?: string;
  /** Whether that period should renew on its own. Decides, at the first
   *  checkout after the trial, between a recurring Cashfree subscription and
   *  a one-time order. Nothing is authorised here. */
  autoRenew?: boolean;
  /** A promotional coupon (docs/platform/coupons.md), validated then redeemed
   *  server-side only once the rest of registration actually succeeds. */
  couponCode?: string;
  /** Emailed to the business address; absent until a superadmin sets up SMTP. */
  otp?: string;
  /** Only true parses on the server: registration is a contract. */
  acceptTerms?: boolean;
}

interface AuthValue {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** The tenant's subscription, refreshed alongside the session. */
  subscription: SubscriptionSummary | null;
  isPlatformAdmin: boolean;
  refreshSubscription: () => Promise<void>;
  /** Re-reads the signed-in session (after a forced password change, say). */
  reloadSession: () => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  signIn: (
    email: string,
    password: string,
    captcha?: CaptchaFields,
    totp?: string,
    /** "Remember me": how long the server keeps this sign-in (see rememberMe.ts). */
    rememberMe?: boolean,
  ) => Promise<void>;
  /** Set when a session ended for a reason the sign-in screen should explain. */
  signedOutReason: string | null;
  signOut: () => Promise<void>;
  /**
   * Phase 8: hands an already-verified offline session straight into
   * context, bypassing the network entirely - the ONLY caller is
   * OfflineUnlock.tsx, after `LocalDatabase.auth.unlockOffline()` has
   * already checked the PIN, the lockout window and the signed capability
   * grant with no server involved. There is no accessToken in this mode
   * (setAccessToken(null) - any accidental api.* call simply gets refused
   * as unauthenticated rather than silently reusing a stale bearer token),
   * no subscription banner (nothing to fetch it from), and no properties
   * list (an offline session is always the one property its local database
   * is pinned to - see EnrolledLocalUser in @kortex/pos-local).
   */
  signInOffline: (session: Session) => void;
  switchProperty: (propertyId: string) => Promise<void>;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  /** Why the last session ended, when it is worth telling somebody. */
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('loading');
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  /**
   * Everything a signed-in session leaves behind on this device, gone
   * (DPDP Phase 9): cached query data (rows from the last person's screens),
   * the local POS source and its open database, the person's local session,
   * and - on the Android shell - the WebView's HTTP cache and form data.
   * The local database itself stays: it belongs to the property, and may
   * hold sales the server has not received yet.
   */
  const clearSessionTraces = useCallback(async () => {
    const userId = sessionRef.current?.user.id;
    queryClient.clear();
    await closePosDataSource(userId).catch(() => undefined);
    await clearNativeWebData('session');
  }, [queryClient]);

  const loadSubscription = useCallback(async () => {
    try {
      setSubscription(await api.get<SubscriptionSummary>('/api/subscription'));
    } catch {
      // Platform staff have no tenant subscription to read; the banner just
      // stays hidden rather than blocking the session.
      setSubscription(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const me = await api.get<Session>('/api/auth/me');
    setSession(me);
    setStatus('authenticated');
    if (me.property) await loadSubscription();
    else setSubscription(null);
  }, [loadSubscription]);

  // On boot, try the refresh cookie so a reload keeps the user signed in.
  useEffect(() => {
    let cancelled = false;
    setUnauthenticatedHandler((reason) => {
      void clearSessionTraces();
      setSession(null);
      setStatus('anonymous');
      setSignedOutReason(
        reason === 'SESSION_IDLE'
          ? 'You were signed out because this screen was left idle. Sign in again.'
          : null,
      );
    });

    void (async () => {
      let refreshed = await api.refresh();
      /*
       * A refresh we could not make is not a session that ended.
       *
       * Rate limiting, a gateway restarting mid-deploy or a flaky connection
       * all answer "ask again", and treating that as a sign-out dumped people
       * on the login screen for reloading a page. One retry covers the blip;
       * past that the boot gives up rather than spinning, and the user can
       * reload - by which point the window has usually moved on.
       */
      if (refreshed === 'unknown' && !cancelled) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        if (!cancelled) refreshed = await api.refresh();
      }
      if (cancelled) return;
      if (refreshed !== true) {
        setStatus('anonymous');
        return;
      }
      try {
        await loadMe();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMe, clearSessionTraces]);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      captcha?: CaptchaFields,
      totp?: string,
      rememberMe?: boolean,
    ) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/login', {
        email,
        password,
        deviceId: deviceIdForAuth(),
        ...(rememberMe === undefined ? {} : { rememberMe }),
        ...(captcha?.captchaId ? { captchaId: captcha.captchaId } : {}),
        ...(captcha?.captchaAnswer ? { captchaAnswer: captcha.captchaAnswer } : {}),
        ...(captcha?.captchaToken ? { captchaToken: captcha.captchaToken } : {}),
        ...(totp ? { totp } : {}),
      });
      setAccessToken(result.accessToken);
      setSignedOutReason(null);
      await loadMe();
    },
    [loadMe],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const result = await api.post<{ accessToken: string }>('/api/public/register', input);
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Offline, or already signed out server-side: still clear this device.
    } finally {
      await clearSessionTraces();
      setAccessToken(null);
      setSession(null);
      setSubscription(null);
      setStatus('anonymous');
    }
  }, [clearSessionTraces]);

  // A sync pull found this user removed, deactivated or no longer POS staff.
  useEffect(() => {
    const onRevoked = () => {
      void signOut().then(() =>
        setSignedOutReason('Your access to this property has changed. Sign in again, or ask an owner.'),
      );
    };
    window.addEventListener(ACCESS_REVOKED_EVENT, onRevoked);
    return () => window.removeEventListener(ACCESS_REVOKED_EVENT, onRevoked);
  }, [signOut]);

  const signInOffline = useCallback((offlineSession: Session) => {
    setAccessToken(null);
    setSession(offlineSession);
    setSignedOutReason(null);
    setSubscription(null);
    setStatus('authenticated');
  }, []);

  const switchProperty = useCallback(
    async (propertyId: string) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/switch-property', {
        propertyId,
        deviceId: deviceIdForAuth(),
      });
      setAccessToken(result.accessToken);
      // The till's local source belongs to the property being left; close it
      // before the new property's session can render anything.
      await closePosDataSource().catch(() => undefined);
      await loadMe();
      // Every cached row was fetched under the property we just left, and no
      // query key mentions the property, so nothing would otherwise refetch -
      // the screen would keep showing the old property's figures under the new
      // property's name. Clearing (rather than invalidating) drops that data
      // instead of displaying it while the refetch is in flight.
      queryClient.clear();
    },
    [loadMe, queryClient],
  );

  const value = useMemo<AuthValue>(
    () => ({
      session,
      status,
      subscription,
      isPlatformAdmin: session ? session.platformRole !== 'NONE' : false,
      refreshSubscription: loadSubscription,
      reloadSession: loadMe,
      register,
      signIn,
      signedOutReason,
      signOut,
      signInOffline,
      switchProperty,
      can: (...permissions) => permissions.every((p) => session?.permissions.includes(p) ?? false),
      canAny: (...permissions) =>
        permissions.some((p) => session?.permissions.includes(p) ?? false),
    }),
    [
      session,
      status,
      subscription,
      signedOutReason,
      loadSubscription,
      register,
      signIn,
      signOut,
      signInOffline,
      switchProperty,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** Renders children only when the signed-in role holds the permission. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission | Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  return <>{can(...list) ? children : fallback}</>;
}
